import { useState } from "react";
import { createFileRoute, redirect, useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { AuthLayout } from "@/features/auth/components/AuthLayout";
import { getAccessKey, isStaleSessionError } from "@/lib/api/client";
import { useAuth } from "@/providers/auth-provider";
import { StaleSessionNotice } from "@/features/account/components/StaleSessionNotice";
import { CliAuthorizePanel } from "@/features/cli/components/CliAuthorizePanel";
import { CodesAtRiskReminder } from "@/features/cli/components/CodesAtRiskReminder";
import { useResolveActivationCode } from "@/features/cli/hooks/useCli";
import type { CliAuthorizationMetadata } from "@/features/cli/api/cli.api";

const searchSchema = z.object({
  user_code: z.string().catch(""),
});

function activatePath(userCode: string): string {
  return userCode ? `/activate?user_code=${encodeURIComponent(userCode)}` : "/activate";
}

export const Route = createFileRoute("/activate")({
  validateSearch: searchSchema,
  beforeLoad: ({ search }) => {
    if (!getAccessKey()) {
      throw redirect({ to: "/login", search: { redirect: activatePath(search.user_code) } });
    }
  },
  component: ActivatePage,
});

function ActivatePage() {
  const { t } = useTranslation();
  const { user_code: prefilled } = useSearch({ from: "/activate" });
  const navigate = useNavigate();
  const { logout } = useAuth();
  const resolve = useResolveActivationCode();
  const [code, setCode] = useState(prefilled);
  const [authorization, setAuthorization] = useState<CliAuthorizationMetadata | null>(null);

  const reauthenticate = () => {
    logout();
    navigate({ to: "/login", search: { redirect: activatePath(code) } });
  };

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (resolve.isPending) return;
    const trimmed = code.trim();
    if (!trimmed) return;
    resolve.mutate(trimmed, { onSuccess: setAuthorization });
  };

  if (authorization) {
    return (
      <AuthLayout>
        <CliAuthorizePanel
          authorization={authorization}
          isLoading={false}
          error={null}
          onReauthenticate={reauthenticate}
        />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="rounded-xl border border-border bg-surface p-6">
        <h1 className="text-lg font-semibold text-heading">{t("cli.activate.title")}</h1>
        <p className="mt-2 text-sm text-muted">{t("cli.activate.subtitle")}</p>

        <form onSubmit={submit} className="mt-6">
          <label htmlFor="user-code" className="block text-sm font-medium text-heading">
            {t("cli.activate.codeLabel")}
          </label>
          <input
            id="user-code"
            name="user-code"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            placeholder={t("cli.activate.codePlaceholder")}
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            readOnly={resolve.isPending}
            aria-disabled={resolve.isPending || undefined}
            className="mt-2 w-full rounded-lg border border-border bg-elevated px-3.5 py-2.5 text-center font-mono text-lg tracking-widest text-heading aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
          />

          {isStaleSessionError(resolve.error) ? null : resolve.error ? (
            <p
              className="mt-4 rounded-lg border border-danger/40 bg-danger/10 p-3 text-sm text-danger"
              role="alert"
            >
              {resolve.error.status === 404
                ? t("cli.activate.unknownCode")
                : resolve.error.status === 429
                  ? t("cli.activate.tooMany")
                  : (resolve.error.error ?? t("common.error"))}
            </p>
          ) : null}

          {isStaleSessionError(resolve.error) ? (
            <StaleSessionNotice
              returnTo="/"
              className="mt-4"
              body={t("cli.authorize.staleSessionBody")}
              fullWidth
              autoFocus
              onSignInAgain={reauthenticate}
            />
          ) : (
            <button
              type="submit"
              disabled={!resolve.isPending && code.trim().length === 0}
              aria-disabled={resolve.isPending || undefined}
              aria-busy={resolve.isPending || undefined}
              data-autofocus={resolve.isPending ? true : undefined}
              className="mt-4 w-full rounded-lg bg-indigo px-4 py-2.5 text-sm font-medium text-white disabled:opacity-60 aria-disabled:opacity-60 aria-disabled:cursor-not-allowed"
            >
              {resolve.isPending ? t("cli.activate.checking") : t("cli.activate.continue")}
            </button>
          )}
        </form>
        <CodesAtRiskReminder />
      </div>
    </AuthLayout>
  );
}
